import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import TrainerLayout from './TrainerLayout';
import { TrainerAntragBadge, TrainerAntragWarnung } from './TrainerAntrag';
import TrainerTeam from './TrainerTeam';

function fmtZeit(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

// 'YYYY-MM-DD' → „Montag, 05.10.2026" (Mittag in UTC, damit der Tag nicht springt)
function fmtDatum(iso) {
  if (!iso) return '';
  return new Date(`${String(iso).slice(0, 10)}T12:00:00Z`)
    .toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}

const ABWESENHEITSGRUENDE = [
  { key: 'krank',     label: 'Krank' },
  { key: 'urlaub',    label: 'Urlaub' },
  { key: 'termin',    label: 'Termin' },
  { key: 'sonstiges', label: 'Sonstiges' },
];

export default function TrainerEinheit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [einheit, setEinheit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('anwesenheit');
  const [toggling, setToggling] = useState({});

  const load = useCallback(() => {
    api.get(`/api/trainer/einheiten/${id}`)
      .then(r => setEinheit(r.data))
      .catch(() => setError('Einheit konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleAnwesenheit = async (schwimmerId, anwesend) => {
    setToggling(t => ({ ...t, [schwimmerId]: true }));
    try {
      await api.post('/api/trainer/anwesenheit', {
        einheit_id: Number(id),
        schwimmer_id: schwimmerId,
        anwesend,
      });
      setEinheit(e => ({
        ...e,
        anwesenheit: e.anwesenheit.map(a =>
          a.schwimmer_id === schwimmerId
            ? { ...a, anwesend, entschuldigt: anwesend ? false : a.entschuldigt, abwesenheitsgrund: anwesend ? null : a.abwesenheitsgrund }
            : a
        ),
      }));
    } catch {
      setError('Anwesenheit konnte nicht gespeichert werden');
    } finally {
      setToggling(t => ({ ...t, [schwimmerId]: false }));
    }
  };

  const handleEntschuldigt = async (schwimmerId, entschuldigt, abwesenheitsgrund) => {
    try {
      await api.post('/api/trainer/anwesenheit', {
        einheit_id: Number(id),
        schwimmer_id: schwimmerId,
        anwesend: false,
        entschuldigt,
        abwesenheitsgrund: abwesenheitsgrund || null,
      });
      setEinheit(e => ({
        ...e,
        anwesenheit: e.anwesenheit.map(a =>
          a.schwimmer_id === schwimmerId ? { ...a, entschuldigt, abwesenheitsgrund: abwesenheitsgrund || null } : a
        ),
      }));
    } catch {
      setError('Änderung konnte nicht gespeichert werden');
    }
  };

  const anwesendCount = (einheit?.anwesenheit || []).filter(a => a.anwesend).length;
  const totalCount = (einheit?.anwesenheit || []).length;
  // Trainings werden nur erfasst, nicht beendet – Anwesenheit und Team bleiben jederzeit änderbar
  const heute = new Date().toLocaleDateString('sv-SE');
  const istHeute = einheit && String(einheit.datum).slice(0, 10) === heute;

  return (
    <TrainerLayout
      title={einheit?.gruppe_name || 'Training'}
      backTo={einheit ? `/trainer/gruppen/${einheit.gruppe_id}` : '/trainer/gruppen'}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} />
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {einheit && !loading && (
        <>
          {/* Status-Leiste */}
          <div style={{
            background: istHeute ? '#e3f2fd' : '#fff8e1',
            borderRadius: 10, padding: '0.75rem 1rem',
            marginBottom: '0.875rem', fontSize: '0.85rem',
          }}>
            {/* Gruppe und Tag stehen immer vollständig da – mehrere Gruppen heißen ähnlich */}
            <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 2 }}>{einheit.gruppe_name}</div>
            <div style={{ fontWeight: 600, color: istHeute ? '#1565c0' : '#e65100', marginBottom: 4 }}>
              {istHeute ? `Training heute · ${fmtDatum(einheit.datum)}` : `Nachtrag für ${fmtDatum(einheit.datum)}`}
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', color: '#555' }}>
              <span>erfasst um {fmtZeit(einheit.gestartet_am)} Uhr</span>
              <span style={{ fontWeight: 600 }}>Anwesend: {anwesendCount}/{totalCount}</span>
            </div>
          </div>

          <TrainerAntragWarnung kinder={einheit.anwesenheit} />

          {/* Tabs */}
          <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', marginBottom: '0.875rem', border: '1px solid #e0e0e0' }}>
            {[['anwesenheit', '✓ Anwesenheit'], ['team', '👥 Team'], ['fortschritt', '📋 Fortschritt']].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)}
                style={{
                  flex: 1, padding: '0.7rem', border: 'none', cursor: 'pointer',
                  fontWeight: tab === key ? 700 : 400, fontSize: '0.9rem',
                  background: tab === key ? '#1565c0' : '#fff',
                  color: tab === key ? '#fff' : '#333',
                }}>
                {label}
              </button>
            ))}
          </div>

          {/* Anwesenheits-Tab */}
          {tab === 'anwesenheit' && (
            <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              {/* Alle an/abwesend */}
              {(
                <div style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem', borderBottom: '1px solid #f0f0f0' }}>
                  <button onClick={() => einheit.anwesenheit.forEach(a => !a.anwesend && handleAnwesenheit(a.schwimmer_id, true))}
                    style={{ flex: 1, padding: '0.5rem', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
                    ✓ Alle anwesend
                  </button>
                  <button onClick={() => einheit.anwesenheit.forEach(a => a.anwesend && handleAnwesenheit(a.schwimmer_id, false))}
                    style={{ flex: 1, padding: '0.5rem', background: '#f5f5f5', color: '#555', border: '1px solid #e0e0e0', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
                    ✗ Alle abwesend
                  </button>
                </div>
              )}

              {(einheit.anwesenheit || []).map((a, i) => (
                <React.Fragment key={a.schwimmer_id}>
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.875rem 1rem', cursor: 'pointer',
                      borderBottom: !a.anwesend || i < einheit.anwesenheit.length - 1 ? '1px solid #f5f5f5' : 'none',
                      background: a.anwesend ? '#f9fff9' : '#fff',
                    }}
                    onClick={() => !toggling[a.schwimmer_id] && handleAnwesenheit(a.schwimmer_id, !a.anwesend)}
                  >
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: a.anwesend ? '#4caf50' : '#e0e0e0',
                      color: '#fff', fontSize: '1.2rem', fontWeight: 700,
                      transition: 'background 0.15s',
                    }}>
                      {toggling[a.schwimmer_id] ? '…' : a.anwesend ? '✓' : '✗'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '1rem' }}>{a.vorname} {a.nachname}</div>
                      <div style={{ fontSize: '0.75rem', color: a.anwesend ? '#4caf50' : '#999', fontWeight: 600 }}>
                        {a.anwesend ? 'Anwesend' : a.entschuldigt ? 'Abwesend · entschuldigt' : 'Abwesend'}
                      </div>
                      <TrainerAntragBadge s={a} />
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/trainer/schwimmer/${a.schwimmer_id}`); }}
                      style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 8, padding: '0.3rem 0.5rem', fontSize: '0.78rem', cursor: 'pointer', color: '#1565c0' }}>
                      📋
                    </button>
                  </div>

                  {/* Entschuldigt-Option nur bei abwesenden Schwimmern, während Einheit läuft */}
                  {!a.anwesend && (
                    <div style={{
                      padding: '0.5rem 1rem 0.75rem 1rem',
                      borderBottom: i < einheit.anwesenheit.length - 1 ? '1px solid #f5f5f5' : 'none',
                      display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
                      background: '#fff',
                    }}>
                      <button
                        onClick={() => handleEntschuldigt(a.schwimmer_id, !a.entschuldigt, a.entschuldigt ? null : a.abwesenheitsgrund)}
                        style={{
                          padding: '0.3rem 0.7rem', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600,
                          cursor: 'pointer',
                          background: a.entschuldigt ? '#fff8e1' : '#f5f5f5',
                          color: a.entschuldigt ? '#f57f17' : '#888',
                          border: `1px solid ${a.entschuldigt ? '#ffe082' : '#e0e0e0'}`,
                        }}>
                        {a.entschuldigt ? '☑ Entschuldigt' : '☐ Entschuldigt'}
                      </button>
                      {a.entschuldigt && (
                        <select
                          value={a.abwesenheitsgrund || ''}
                          onChange={e => handleEntschuldigt(a.schwimmer_id, true, e.target.value || null)}
                          style={{
                            padding: '0.3rem 0.5rem', borderRadius: 8, fontSize: '0.78rem',
                            border: '1px solid #e0e0e0', color: '#555', background: '#fff',
                          }}>
                          <option value="">Grund wählen…</option>
                          {ABWESENHEITSGRUENDE.map(g => (
                            <option key={g.key} value={g.key}>{g.label}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Team-Tab: Übungsleiter und Helfer einchecken */}
          {tab === 'team' && <TrainerTeam einheitId={id} />}

          {/* Fortschritts-Tab */}
          {tab === 'fortschritt' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {(einheit.anwesenheit || []).filter(a => a.anwesend).map(a => (
                <button key={a.schwimmer_id}
                  onClick={() => navigate(`/trainer/schwimmer/${a.schwimmer_id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.875rem 1rem', background: '#fff', border: '1px solid #e0e0e0',
                    borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', background: '#e3f2fd',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, color: '#1565c0', fontSize: '0.85rem', flexShrink: 0,
                  }}>
                    {a.vorname?.[0]}{a.nachname?.[0]}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{a.vorname} {a.nachname}</div>
                    <div style={{ fontSize: '0.75rem', color: '#888' }}>Übungen ansehen →</div>
                  </div>
                  <span style={{ color: '#bbb', fontSize: '1.2rem' }}>›</span>
                </button>
              ))}
              {(einheit.anwesenheit || []).filter(a => a.anwesend).length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#999', fontSize: '0.85rem' }}>
                  Noch niemand als anwesend markiert
                </div>
              )}
            </div>
          )}

          {/* Alles wird sofort gespeichert – kein Beenden nötig */}
          <div style={{ marginTop: '1.25rem' }}>
            <button onClick={() => navigate(`/trainer/gruppen/${einheit.gruppe_id}`)}
              style={{
                width: '100%', padding: '0.875rem', background: '#fff', color: '#1565c0',
                border: '1px solid #e0e0e0', borderRadius: 12, fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
              }}>
              ✓ Fertig – zurück zur Gruppe
            </button>
            <div style={{ textAlign: 'center', fontSize: '0.78rem', color: '#888', marginTop: '0.5rem' }}>
              Jede Änderung ist sofort gespeichert. Anwesenheit und Team lassen sich auch später noch ergänzen.
            </div>
          </div>
        </>
      )}
    </TrainerLayout>
  );
}
